import type { Element } from "@xmpp/xml"
import { parseHybridJsonResponse } from "@/components/cmrReport/parseHybridJsonResponse"
import type {
  AlertDetail,
  DriverFuelRow,
  FuelAlertEvent,
  LatLng,
  RefuelEvent,
  RefuelOut,
  Seen,
  TelemetryPoint,
  Verdict,
} from "./fuelReportTypes"

const VERDICTS: Verdict[] = [
  "MATCH",
  "ELSEWHERE",
  "NO_REFUEL",
  "NO_SUGGESTION",
  "ERROR",
]
function asVerdict(v: unknown): Verdict {
  return VERDICTS.includes(v as Verdict) ? (v as Verdict) : "ERROR"
}
function num(v: unknown): number | null {
  return typeof v === "number" && !Number.isNaN(v) ? v : null
}
const STATION_STR_META = [
  "name",
  "brand",
  "source",
  "address",
  "city",
  "category",
  "country",
  "currency",
] as const
function latLng(v: unknown): LatLng | null {
  if (v && typeof v === "object" && "lat" in v && "lng" in v) {
    const o = v as Record<string, unknown>
    const lat = num(o.lat)
    const lng = num(o.lng)
    if (lat !== null && lng !== null) {
      const out: LatLng = { lat, lng }
      for (const key of STATION_STR_META) {
        if (typeof o[key] === "string" && o[key]) out[key] = o[key] as string
      }
      const detourKm = num(o.detour_km)
      if (detourKm !== null) out.detourKm = detourKm
      const detourMin = num(o.detour_min)
      if (detourMin !== null) out.detourMin = detourMin
      const pricePerLitre = num(o.price_per_litre)
      if (pricePerLitre !== null) out.pricePerLitre = pricePerLitre
      if (typeof o.fuel_partner === "string" && o.fuel_partner) {
        out.fuelPartner = o.fuel_partner
      }
      if (typeof o.google_maps_url === "string" && o.google_maps_url) {
        out.googleMapsUrl = o.google_maps_url
      }
      return out
    }
  }
  return null
}
function latLngList(v: unknown): LatLng[] {
  return Array.isArray(v) ? v.map(latLng).filter((x): x is LatLng => !!x) : []
}

type RawAlert = Record<string, unknown>
type RawRefuel = Record<string, unknown>
type RawDriver = Record<string, unknown>

function mapAlert(a: RawAlert): FuelAlertEvent {
  return {
    id: String(a.id ?? ""),
    sentAt: String(a.sent_at ?? ""),
    lat: num(a.lat),
    lon: num(a.lon),
    fuelLevel: num(a.fuel_level),
    selectedStation: latLng(a.selected_station),
    suggestedStations: latLngList(a.suggested_stations),
    verdict: asVerdict(a.verdict),
    linkedRefuelId: a.linked_refuel_id ? String(a.linked_refuel_id) : null,
    messageId: a.message_id ? String(a.message_id) : undefined,
    sentDayBefore: Boolean(a.sent_day_before),
    suppressed: Boolean(a.suppressed),
    reason: a.reason ? String(a.reason) : null,
    seen: typeof a.seen === "boolean" ? a.seen : undefined,
    seenAt:
      typeof a.seen === "boolean"
        ? a.seen_at
          ? String(a.seen_at)
          : null
        : undefined,
  }
}

// The server-side "seen" block from get_fuel_alert_detail: {found, seen,
// seen_at, seen_pos:{lat,lon}, gps_sample_gap_s}. Null when absent.
function mapSeen(v: unknown): Seen | null {
  if (!v || typeof v !== "object") return null
  const o = v as Record<string, unknown>
  const pos =
    o.seen_pos && typeof o.seen_pos === "object"
      ? (o.seen_pos as Record<string, unknown>)
      : null
  return {
    found: Boolean(o.found),
    seen: Boolean(o.seen),
    seenAt: o.seen_at ? String(o.seen_at) : null,
    seenLat: pos ? num(pos.lat) : null,
    seenLng: pos ? num(pos.lon) : null,
    gpsSampleGapS: num(o.gps_sample_gap_s),
  }
}

function mapRefuel(r: RawRefuel): RefuelEvent {
  return {
    id: String(r.id ?? ""),
    refueledAt: String(r.refueled_at ?? ""),
    lat: num(r.lat),
    lon: num(r.lon),
    fuelBefore: num(r.fuel_before),
    fuelAfter: num(r.fuel_after),
    fuelLitersBefore: num(r.fuel_liters_before),
    fuelLitersAfter: num(r.fuel_liters_after),
    verdict: asVerdict(r.verdict),
    matchedStation: latLng(r.matched_station),
    matchDistanceM: num(r.match_distance_m),
    linkedAlertId: r.linked_alert_id ? String(r.linked_alert_id) : null,
  }
}

export async function parseFuelReport(
  result: Element,
): Promise<DriverFuelRow[]> {
  const payload = await parseHybridJsonResponse<{ drivers?: RawDriver[] }>(
    result,
  )
  const drivers = payload.drivers ?? []
  return drivers.map((d) => ({
    driverJid: String(d.driver_jid ?? ""),
    chatJid: d.chat_jid ? String(d.chat_jid) : null,
    alerts: Array.isArray(d.alerts)
      ? (d.alerts as RawAlert[]).map(mapAlert)
      : [],
    refuels: Array.isArray(d.refuels)
      ? (d.refuels as RawRefuel[]).map(mapRefuel)
      : [],
  }))
}

function mapRefuelOut(
  r: Record<string, unknown> | null | undefined,
): RefuelOut | null {
  if (!r) return null
  return {
    ts: String(r.ts ?? ""),
    lat: num(r.lat),
    lng: num(r.lng),
    fuelBefore: num(r.fuel_before),
    fuelAfter: num(r.fuel_after),
    closest: latLng(r.closest),
    distanceM: num(r.distance_m),
  }
}

export async function parseAlertDetail(result: Element): Promise<AlertDetail> {
  const p = await parseHybridJsonResponse<Record<string, unknown>>(result)
  return {
    verdict: asVerdict(p.verdict),
    refuel: mapRefuelOut(p.refuel as Record<string, unknown> | null),
    suggested: latLngList(p.suggested),
    driverPos: latLng(p.driver_pos),
    seen: mapSeen(p.seen),
  }
}

export async function parseTelemetry(
  result: Element,
): Promise<TelemetryPoint[]> {
  const p = await parseHybridJsonResponse<{
    points?: Record<string, unknown>[]
  }>(result)
  const pts = (p.points ?? []).map((pt) => ({
    lat: num(pt.lat) ?? 0,
    lng: num(pt.lng) ?? 0,
    timestamp: String(pt.timestamp ?? ""),
    fuelLevel: num(pt.fuel_level),
  }))
  return pts.sort((a, b) => a.timestamp.localeCompare(b.timestamp))
}
